import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import './AdminCookies.css';
import { motion, AnimatePresence } from 'framer-motion';
import { FaSave, FaPlus, FaTrash, FaCookieBite, FaHeading, FaParagraph, FaListUl } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { safeFetch } from '../utils/apiUtils';

const AdminCookies = () => {
    const [isSaving, setIsSaving] = useState(false);
    const [policyId, setPolicyId] = useState(null);
    const [cookiesData, setCookiesData] = useState({
        hero: {
            titlePart: 'Cookie',
            highlight: 'Policy',
            description: 'We use cookies to enhance your experience and provide personalized service. Learn how we use them here.'
        },
        sections: [
            {
                id: Date.now(),
                icon: 'cookie',
                title: 'What Are Cookies?',
                content: 'Cookies are small text files that are placed on your computer or mobile device when you browse websites. They are widely used to make websites work more efficiently, as well as to provide information to the owners of the site.',
                points: []
            }
        ],
        lastUpdated: 'February 6, 2026'
    });

    useEffect(() => {
        fetchPolicy();
    }, []);

    const fetchPolicy = async () => {
        try {
            const { ok, data: result } = await safeFetch('https://aayushrealproperties.in/nimaibackend/public/api/cookie-policies', {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
                    'Accept': 'application/json'
                }
            });
            let policy = null;

            if (Array.isArray(result)) {
                policy = result[0];
            } else if (result.data) {
                policy = Array.isArray(result.data) ? result.data[0] : result.data;
            }

            if (ok && policy) {
                setPolicyId(policy.id);
                setCookiesData({
                    hero: {
                        titlePart: policy.hero_title_part,
                        highlight: policy.hero_highlight_word,
                        description: policy.hero_description
                    },
                    sections: policy.sections.map((s, idx) => ({
                        id: idx,
                        icon: s.icon_type,
                        title: s.section_title,
                        content: s.content,
                        points: s.list_items || []
                    })),
                    lastUpdated: policy.last_updated_date
                });
            }
        } catch (error) {
            console.error('Error fetching policy:', error);
            toast.error('Failed to load cookie policy');
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        const method = policyId ? 'PUT' : 'POST';
        const url = policyId
            ? `https://aayushrealproperties.in/nimaibackend/public/api/cookie-policies/${policyId}`
            : 'https://aayushrealproperties.in/nimaibackend/public/api/cookie-policies';

        const payload = {
            hero_title_part: cookiesData.hero.titlePart,
            hero_highlight_word: cookiesData.hero.highlight,
            hero_description: cookiesData.hero.description,
            last_updated_date: cookiesData.lastUpdated,
            sections: cookiesData.sections.map(s => ({
                icon_type: s.icon,
                section_title: s.title,
                content: s.content,
                list_items: s.points
            }))
        };

        try {
            const { ok, data: result } = await safeFetch(url, {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
                    'Accept': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (ok) {
                if (!policyId) setPolicyId(result.id);
                toast.success('Cookie Policy updated successfully!');
            } else {
                toast.error('Failed to update cookie policy');
            }
        } catch (error) {
            console.error('Error saving policy:', error);
            toast.error('Network error while saving');
        } finally {
            setIsSaving(false);
        }
    };

    const addSection = () => {
        const newSection = {
            id: Date.now(),
            icon: 'cookie',
            title: 'New Section Title',
            content: 'Section content goes here...',
            points: []
        };
        setCookiesData({ ...cookiesData, sections: [...cookiesData.sections, newSection] });
    };

    const removeSection = (id) => {
        setCookiesData({ ...cookiesData, sections: cookiesData.sections.filter(s => s.id !== id) });
    };

    const updateSection = (id, field, value) => {
        const newSections = cookiesData.sections.map(s => {
            if (s.id === id) return { ...s, [field]: value };
            return s;
        });
        setCookiesData({ ...cookiesData, sections: newSections });
    };

    const addPoint = (sectionId) => {
        const newSections = cookiesData.sections.map(s => {
            if (s.id === sectionId) return { ...s, points: [...s.points, 'New point'] };
            return s;
        });
        setCookiesData({ ...cookiesData, sections: newSections });
    };

    const removePoint = (sectionId, pointIdx) => {
        const newSections = cookiesData.sections.map(s => {
            if (s.id === sectionId) return { ...s, points: s.points.filter((_, i) => i !== pointIdx) };
            return s;
        });
        setCookiesData({ ...cookiesData, sections: newSections });
    };

    const updatePoint = (sectionId, pointIdx, value) => {
        const newSections = cookiesData.sections.map(s => {
            if (s.id === sectionId) {
                const newPoints = [...s.points];
                newPoints[pointIdx] = value;
                return { ...s, points: newPoints };
            }
            return s;
        });
        setCookiesData({ ...cookiesData, sections: newSections });
    };

    return (
        <div className="admin-layout">
            <Sidebar />
            <main className="admin-content">
                <header className="content-header">
                    <div>
                        <h1>Edit <span>Cookie Policy</span></h1>
                        <p>Manage how you inform users about cookie usage and tracking.</p>
                    </div>
                    <div className="header-actions">
                        <button className="save-btn" onClick={handleSave} disabled={isSaving}>
                            {isSaving ? 'Saving...' : <><FaSave /> Save Changes</>}
                        </button>
                    </div>
                </header>

                <div className="admin-cookies-container">
                    {/* Hero Section Edit */}
                    <div className="admin-card-section">
                        <div className="section-header">
                            <h3><FaHeading /> Hero Section</h3>
                        </div>
                        <div className="admin-form-grid">
                            <div className="admin-input-group">
                                <label>Title Part</label>
                                <input type="text" value={cookiesData.hero.titlePart} onChange={(e) => setCookiesData({ ...cookiesData, hero: { ...cookiesData.hero, titlePart: e.target.value } })} />
                            </div>
                            <div className="admin-input-group">
                                <label>Highlight Word</label>
                                <input type="text" className="highlight-field" value={cookiesData.hero.highlight} onChange={(e) => setCookiesData({ ...cookiesData, hero: { ...cookiesData.hero, highlight: e.target.value } })} />
                            </div>
                            <div className="admin-input-group full-width">
                                <label>Description</label>
                                <textarea rows="3" value={cookiesData.hero.description} onChange={(e) => setCookiesData({ ...cookiesData, hero: { ...cookiesData.hero, description: e.target.value } })}></textarea>
                            </div>
                        </div>
                    </div>

                    {/* Policy Sections Edit */}
                    <div className="sections-list">
                        <div className="sections-header">
                            <h3><FaCookieBite /> Cookie Policy Sections</h3>
                            <button className="add-section-btn" onClick={addSection}><FaPlus /> Add New Section</button>
                        </div>

                        <AnimatePresence>
                            {cookiesData.sections.map((section, index) => (
                                <motion.div
                                    key={section.id}
                                    className="admin-cookie-section-card"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, x: -50 }}
                                >
                                    <div className="card-top">
                                        <h4>Section #{index + 1}</h4>
                                        <button className="delete-btn" onClick={() => removeSection(section.id)}><FaTrash /></button>
                                    </div>

                                    <div className="admin-form-grid">
                                        <div className="admin-input-group">
                                            <label>Icon Type</label>
                                            <select value={section.icon} onChange={(e) => updateSection(section.id, 'icon', e.target.value)}>
                                                <option value="cookie">Cookie (Definition)</option>
                                                <option value="info">Info (Usage)</option>
                                                <option value="chart">Chart (Analytics)</option>
                                                <option value="cogs">Cogs (Management)</option>
                                                <option value="shield">Shield (Security)</option>
                                            </select>
                                        </div>
                                        <div className="admin-input-group">
                                            <label>Section Title</label>
                                            <input type="text" value={section.title} onChange={(e) => updateSection(section.id, 'title', e.target.value)} />
                                        </div>
                                        <div className="admin-input-group full-width">
                                            <label><FaParagraph /> Content</label>
                                            <textarea rows="4" value={section.content} onChange={(e) => updateSection(section.id, 'content', e.target.value)}></textarea>
                                        </div>

                                        <div className="admin-input-group full-width">
                                            <label><FaListUl /> List Items (Optional)</label>
                                            <div className="points-manager">
                                                {section.points.map((point, pIdx) => (
                                                    <div key={pIdx} className="point-row">
                                                        <input type="text" value={point} onChange={(e) => updatePoint(section.id, pIdx, e.target.value)} />
                                                        <button className="remove-point" onClick={() => removePoint(section.id, pIdx)}><FaTrash /></button>
                                                    </div>
                                                ))}
                                                <button className="add-point-btn" onClick={() => addPoint(section.id)}><FaPlus /> Add Point</button>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </div>

                    <div className="admin-card-section footer-meta">
                        <div className="admin-input-group">
                            <label>Last Updated Date</label>
                            <input type="text" value={cookiesData.lastUpdated} onChange={(e) => setCookiesData({ ...cookiesData, lastUpdated: e.target.value })} />
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default AdminCookies;
